"use client";
import { DEMO_MODE } from "@/lib/demo";


import { ReactNode, useEffect, useLayoutEffect } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useHydrateAtoms } from "jotai/react/utils";
import { queryClientAtom } from "jotai-tanstack-query";
import { HeroUIProvider } from "@heroui/system";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { app as fireApp, firebaseConfig } from '@/lib/firebase.config';
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes"
import { getMessaging, onMessage, isSupported as isMessasgingSupported } from "firebase/messaging";
import { ConfigProvider, theme } from "antd";
import { AntdRegistry } from "@ant-design/nextjs-registry";

import AOS from 'aos';

export const queryClient = new QueryClient();

const HydrateAtoms = ({ children }: { children: ReactNode }) => {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
};

const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  useLayoutEffect(() => {
    if (DEMO_MODE) return;
    const checkEnvironment = async () => {
      const supported = await isAnalyticsSupported()
      if (supported) getAnalytics(fireApp);
    }

    checkEnvironment()
  }, [])

  return children;
};

const MessagingProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    if (DEMO_MODE) return;
    const checkEnvironment = async () => {
      const supported = await isMessasgingSupported()
      if (!supported) return;

      await Notification.requestPermission();

      const messaging = getMessaging(fireApp);

      onMessage(messaging, ({ notification }) => {
        // console.log("FOME", notification)
        if (notification) new Notification(notification.title || "", {
          body: notification.body,
          icon: "/favicon-32x32.png",
        });
      });

      if ("serviceWorker" in navigator) {
        await navigator.serviceWorker.register(`/firebase-messaging-sw.js`, {
          scope: '/',
          updateViaCache: 'none',
        })
        // .then((registration) => console.log("Service Worker registered:", registration))
        // .catch((error) => console.error("Service Worker registration failed:", error));
      }
    }

    checkEnvironment()
  }, [])

  return children;
};



interface ChildProviderProps { children: ReactNode }
export const ChildProvider = ({
  children,
}: ChildProviderProps) => {
  const { theme: currentTheme, setTheme } = useTheme()
  return (
    <ConfigProvider theme={{
      algorithm: currentTheme == "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm
    }}>
      <main>{children}</main>
    </ConfigProvider>

  )
}


export const GlobalProviders = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    if (DEMO_MODE) return;
    AOS.init({ duration: 700, once: true });
  }, []);

  return (
    <AnalyticsProvider>
      <MessagingProvider>
        <JotaiProvider>
          <QueryClientProvider client={queryClient}>
            <HydrateAtoms>
              <AntdRegistry>
                <HeroUIProvider>
                  <ThemeProvider attribute="class" defaultTheme="light">
                    <ChildProvider>
                      <TooltipProvider>
                        <SidebarProvider defaultOpen={false}>{children}</SidebarProvider>
                      </TooltipProvider>
                    </ChildProvider>
                  </ThemeProvider>
                </HeroUIProvider>
              </AntdRegistry>
            </HydrateAtoms>
          </QueryClientProvider>
        </JotaiProvider>
      </MessagingProvider>
    </AnalyticsProvider>
  );
};

export function ThemeProvider({
  children,
  ...props
}: React.ComponentProps<typeof NextThemesProvider>) {
  return <NextThemesProvider defaultTheme="light" enableSystem={false} {...props}>{children}</NextThemesProvider>
}
